import posthog from 'posthog-js';

// Environment validation: Only allow production domain (app.surveilx.net)
const isAllowedEnvironment = () => {
  const hostname = window.location.hostname;

  // Debug mode: Allow testing on any environment if ?posthogDebug=true
  const debugMode = new URLSearchParams(window.location.search).get('posthogDebug') === 'true';
  
  // Only allow production domain, block staging IP, localhost, and other domains
  const isProduction = hostname === 'app.surveilx.net';

  return isProduction || debugMode;
};

export const initPostHog = () => {
  const posthogKey = process.env.REACT_APP_POSTHOG_KEY;
  const posthogHost = process.env.REACT_APP_POSTHOG_HOST;

  if (!posthogKey) {
    console.warn('PostHog key not found in environment variables');
    return;
  }

  const isAllowed = isAllowedEnvironment();
  console.log('🔍 PostHog Environment Check:', {
    hostname: window.location.hostname,
    pathname: window.location.pathname,
    debugMode: new URLSearchParams(window.location.search).get('posthogDebug') === 'true',
    isAllowed,
  });

  if (!isAllowed) {
    console.log('⛔ PostHog disabled: Not in allowed environment');
    return;
  }

  try {
    console.log('🔍 PostHog Initialization Starting...');
    console.log('📌 PostHog Key:', posthogKey.substring(0, 10) + '...');
    console.log('📌 PostHog Host:', posthogHost);

    posthog.init(posthogKey, {
      api_host: posthogHost || 'https://us.i.posthog.com',
      loaded: (ph) => {
        console.log('✅ PostHog loaded callback triggered');
        window.posthog = ph;
        console.log('✅ PostHog attached to window.posthog');
      },
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: false,
      disable_session_recording: false,
      persistence: 'localStorage',
      request_timeout: 10000,
      batch_size: 1,
      batch_timeout: 0,
      enable_recording_console_log: false,
      enable_heatmaps: false,
      capture_dead_clicks: false,
      capture_performance: false,
    });

    // Explicitly set window.posthog
    window.posthog = posthog;
    console.log('✅ window.posthog set to posthog instance');

    if (window.posthog) {
      console.log('✅ window.posthog is available');
      posthog.capture('app_initialized', {
        timestamp: new Date().toISOString(),
        environment: process.env.NODE_ENV,
      });
      console.log('📤 Test event "app_initialized" sent');
    } else {
      console.error('❌ window.posthog is NOT available');
    }

    console.log('📋 PostHog Diagnostics:', {
      isInitialized: !!window.posthog,
      hasCapture: !!window.posthog?.capture,
      hasIdentify: !!window.posthog?.identify,
      persistence: 'localStorage',
    });

  } catch (error) {
    console.error('❌ Failed to initialize PostHog:', error);
  }
};

export const clearPostHogCache = () => {
  try {
    console.log('🧹 Clearing PostHog cache...');
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.includes('posthog')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      console.log('🗑️ Removing PostHog localStorage key:', key);
      localStorage.removeItem(key);
    });
    console.log('✅ PostHog cache cleared');
  } catch (error) {
    console.error('❌ Failed to clear PostHog cache:', error);
  }
};

export const identifyUserInPostHog = (email, userId, additionalProperties = {}) => {
  if (!isAllowedEnvironment()) {
    console.log('⛔ PostHog user identification blocked: Not in allowed environment');
    return;
  }

  if (!email) {
    console.warn('⚠️ Email is required for PostHog identification');
    return;
  }

  try {
    if (!window.posthog || !window.posthog.identify) {
      console.warn('⚠️ PostHog not initialized or identify function not available');
      return;
    }

    const currentDistinctId = window.posthog?.get_distinct_id?.();
    console.log('📊 Current PostHog Distinct ID:', currentDistinctId);
    console.log('📊 New Email to Identify:', email);

    // Only reset if already identified with a DIFFERENT email (not an anonymous UUID)
    const isDifferentEmail = currentDistinctId && currentDistinctId.includes('@') && currentDistinctId !== email;
    if (isDifferentEmail) {
      console.log('🔄 Resetting PostHog before identifying with new email');
      window.posthog.reset();
    }

    // Use email as distinct ID so it displays in PostHog Person column
    window.posthog.identify(email, {
      email: email,
      userId: userId,
      ...additionalProperties,
    });

    if (window.posthog.flush) {
      window.posthog.flush();
    }

    console.log('✅ PostHog user identified with email:', email);
  } catch (error) {
    console.error('❌ Failed to identify user in PostHog:', error);
  }
};

export const diagnosticPostHog = () => {
  const isAllowed = isAllowedEnvironment();
  console.log('🔍 PostHog Diagnostic Report:');
  console.log('✓ Hostname:', window.location.hostname);
  console.log('✓ Pathname:', window.location.pathname);
  console.log('✓ Debug Mode:', new URLSearchParams(window.location.search).get('posthogDebug') === 'true');
  console.log('✓ Environment Allowed:', isAllowed);
  console.log('✓ PostHog Available:', !!window.posthog);
  console.log('✓ Capture Function:', !!window.posthog?.capture);
  console.log('✓ Identify Function:', !!window.posthog?.identify);
  console.log('✓ Session ID:', window.posthog?.get_session_id?.());
  console.log('✓ Distinct ID:', window.posthog?.get_distinct_id?.());
  console.log('✓ Properties:', window.posthog?.getProperties?.());
};

export default posthog;
